import React, { useState, useEffect } from "react";
import TopBar from "../globalComponents/topbar";
import { useNavigate } from "react-router-dom";
import {
  Paper, Typography, Divider,
  Box, Avatar, Button, Card, CardContent,
  CircularProgress, Stack, Chip,
} from "@mui/material";
import {
  Psychology, Groups, People, Flag, Chat,HourglassFull,
  Forum, MenuBook, PlayCircle, Event,
} from "@mui/icons-material";
import Footer from "../globalComponents/footer";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import EventIcon from "@mui/icons-material/Event";
import { alpha } from "@mui/material/styles";

// ── Brand palette ────────────────────────────────────────────────────────────
const C = {
  primary: "#0A2E8A",
  primaryDark: "#061E5C",
  primaryLight: "#3B5BC9",
  primarySoft: "#E8EDFC",
  primarySofter: "#F2F5FE",
  secondary: "#10682A",
  secondaryDark: "#0A4A1C",
  secondaryLight: "#2E9148",
  secondarySoft: "#E3F2E8",
  secondarySofter: "#F0F8F3",
  bg: "#FAFBFC",
  paper: "#FFFFFF",
  border: "#E5E9F0",
  borderSoft: "#EEF1F6",
  textPrimary: "#0D1117",
  textSecondary: "#5B6B7E",
  textMuted: "#8A9BB0",
};

export default () => {
  const navigate = useNavigate();

  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [posts, setPosts] = useState([]);
  const [error, setError] = useState("");
  const [sessions, setSessions] = useState([]);
  const currentPosts = posts.slice(0, 2);

  // Quick actions — no accent needed, all tiles are now white
  const actionsTop = [
    { label: "Mentors", icon: <People fontSize="medium" />, path: "/mentor" },
    { label: "Lessons", icon: <PlayCircle fontSize="medium" />, path: "/curriculum" },
    { label: "Chat", icon: <Chat fontSize="medium" />, path: "/chat" },
    { label: "Events", icon: <Event fontSize="medium" />, path: "/events" },
  ];
  const actionsBottom = [
    { label: "My Goals", icon: <Flag fontSize="medium" />, path: "/mygoal" },
    { label: "Community", icon: <People fontSize="medium" />, path: "/discussionForum" },
    { label: "Sessions", icon: <MenuBook fontSize="medium" />, path: "/sessions" },
    { label: "Requests", icon: <HourglassFull fontSize="medium" />, path: "/learner-requests" },
  ];

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const r = await fetch("https://backend-global-1vq9.onrender.com/api/sessions");
        if (!r.ok) throw new Error("Failed to load sessions");
        const d = await r.json();
        const all = Array.isArray(d.data || d) ? (d.data || d) : [];
        const now = new Date();
        const upcoming = all
          .filter((s) => s.date && new Date(s.date) >= now)
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        setSessions(upcoming.slice(0, 3));
      } catch (e) { setError(e.message); } finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const r = await fetch("https://backend-global-1vq9.onrender.com/api/lessons/all");
        if (!r.ok) throw new Error("Failed to fetch lessons");
        const d = await r.json();
        setLessons(Array.isArray(d.data || d) ? (d.data || d).slice(0, 3) : []);
      } catch (e) { setError(e.message); } finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const r = await fetch("https://backend-global-1vq9.onrender.com/api/posts");
        if (!r.ok) throw new Error("Failed to load posts");
        const d = await r.json();
        setPosts(Array.isArray(d.data || d) ? (d.data || d).reverse() : []);
      } catch (e) { setError(e.message); } finally { setLoading(false); }
    })();
  }, []);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const firstName = user?.fullName?.split(" ")[0] || "there";

  const px = { xs: "12px", sm: "24px", md: "38px" };
  const rowMb = { xs: 2, sm: 3, md: 3.5 };

  // ── Panel: white card with green→blue accent strip on top ────────────────
  const Panel = ({ children, sx = {} }) => (
    <Paper elevation={0} sx={{
      position: "relative",
      borderRadius: 3,
      bgcolor: C.paper,
      border: `1px solid ${C.border}`,
      boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
      boxSizing: "border-box",
      overflow: "hidden",
      p: { xs: 2, sm: 2.5, md: 3 },
      transition: "box-shadow 0.2s ease",
      "&:hover": { boxShadow: "0 4px 18px rgba(13,17,23,0.06)" },
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0, left: 0, right: 0,
        height: "3px",
        background: `linear-gradient(90deg, ${C.secondary}, ${C.primary})`,
      },
      ...sx,
    }}>
      {children}
    </Paper>
  );

  // ── Section header with title + optional view-all ─────────────────────
  const PanelHeader = ({ title, onViewAll, count }) => (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography sx={{
            fontWeight: 700,
            color: C.textPrimary,
            fontSize: { xs: 14, sm: 15, md: 16 },
            letterSpacing: -0.2,
          }}>
            {title}
          </Typography>
          {count !== undefined && count > 0 && (
            <Chip label={count} size="small" sx={{
              height: 20, fontSize: 11, fontWeight: 700,
              bgcolor: C.secondarySoft, color: C.secondary,
              "& .MuiChip-label": { px: 1 },
            }} />
          )}
        </Stack>
        {onViewAll && (
          <Box onClick={onViewAll} sx={{
            display: "flex", alignItems: "center", gap: 0.5,
            cursor: "pointer", flexShrink: 0,
            color: C.secondary,
            transition: "color 0.15s",
            "&:hover": { color: C.secondaryLight },
          }}>
            <Typography sx={{ fontSize: { xs: 11, sm: 12 }, fontWeight: 600 }}>View all</Typography>
            <ArrowForwardIosIcon sx={{ fontSize: { xs: 10, sm: 11 } }} />
          </Box>
        )}
      </Box>
      <Divider sx={{ mb: 1.75, borderColor: C.borderSoft }} />
    </>
  );

  // ── Row item inside panels ────────────────────────────────────────────
  const RowItem = ({ children, onClick }) => (
    <Paper onClick={onClick} elevation={0} sx={{
      display: "flex", alignItems: "center",
      borderRadius: 2,
      border: `1px solid ${C.borderSoft}`,
      p: { xs: "10px 12px", sm: "12px 14px" },
      mb: { xs: 1, sm: 1.25 },
      width: "100%", boxSizing: "border-box",
      gap: { xs: 1, sm: 1.5 },
      overflow: "hidden",
      cursor: onClick ? "pointer" : "default",
      transition: "all 0.15s ease",
      "&:hover": onClick ? {
        borderColor: C.secondarySoft,
        bgcolor: C.secondarySofter,
        transform: "translateX(2px)",
      } : {},
    }}>
      {children}
    </Paper>
  );

  // ── Green CTA button ──────────────────────────────────────────────────
  const SecondaryBtn = ({ label, onClick }) => (
    <Button variant="contained" size="small" onClick={onClick}
      endIcon={<ArrowForwardIosIcon sx={{ fontSize: 10 }} />}
      sx={{
        bgcolor: C.secondary, color: "#fff",
        fontWeight: 700, textTransform: "none",
        borderRadius: 2, flexShrink: 0,
        fontSize: { xs: 10, sm: 12 },
        px: { xs: 1.25, sm: 1.75 }, py: 0.5,
        minWidth: "auto", boxShadow: "none",
        "&:hover": { bgcolor: C.secondaryDark, boxShadow: "0 4px 12px rgba(16,104,42,0.25)" },
      }}
    >
      {label}
    </Button>
  );

  // ── Quick-action tile (matches mentor dashboard style) ─────────────────
  //    White bg · soft-blue icon chip · green label
  const ActionTile = ({ label, icon, onClick }) => (
    <Paper elevation={0} onClick={onClick} sx={{
      position: "relative",
      display: "flex", flexDirection: "column",
      alignItems: "flex-start", justifyContent: "flex-start",
      gap: { xs: 1, sm: 1.25 },
      borderRadius: 2.5,
      bgcolor: C.paper,
      border: `1px solid ${C.border}`,
      p: { xs: "12px", sm: "16px", md: "18px" },
      cursor: "pointer",
      minHeight: { xs: 90, sm: 110, md: 120 },
      userSelect: "none",
      transition: "all 0.2s ease",
      "&:hover": {
        borderColor: alpha(C.primary, 0.3),
        boxShadow: "0 8px 20px rgba(13,17,23,0.08)",
        transform: "translateY(-3px)",
        bgcolor: C.primarySofter,
      },
      // On hover fill the icon box with solid blue
      "&:hover .action-icon": {
        bgcolor: C.primary,
        color: "#fff",
      },
    }}>
      {/* Icon: soft-blue chip */}
      <Box className="action-icon" sx={{
        width: { xs: 34, sm: 38 }, height: { xs: 34, sm: 38 },
        borderRadius: 2,
        bgcolor: C.primarySoft,
        color: C.primary,
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all 0.2s ease",
        flexShrink: 0,
      }}>
        {icon}
      </Box>
      {/* Label: green */}
      <Typography sx={{
        fontWeight: 700,
        color: "#000",
        fontSize: { xs: 12, sm: 13, md: 14 },
        lineHeight: 1.3,
      }}>
        {label}
      </Typography>
    </Paper>
  );

  return (
    <Box sx={{
      width: "100%", maxWidth: "100vw",
      boxSizing: "border-box",
      background: C.bg, minHeight: "100vh",
      display: "flex", flexDirection: "column",
    }}>
      <TopBar />

      {/* ══ HERO ═══════════════════════════════════════════════════════════ */}
      <Box sx={{ px, pt: { xs: 2.5, sm: 3.5 }, pb: { xs: 2.5, sm: 3 }, mb: rowMb }}>
        <Paper elevation={0} sx={{
          position: "relative",
          borderRadius: 3,
          overflow: "hidden",
          background: `linear-gradient(135deg, ${C.secondary} 0%, ${C.primaryDark} 100%)`,
          p: { xs: 3, sm: 4, md: 5 },
          color: "#fff",
          boxShadow: "0 6px 24px rgba(10,46,138,0.18)",
        }}>
          <Box sx={{
            position: "absolute",
            top: "-40%", right: "-10%",
            width: { xs: 200, sm: 280 }, height: { xs: 200, sm: 280 },
            borderRadius: "50%",
            background: `radial-gradient(circle, ${C.primary}40 0%, transparent 70%)`,
            pointerEvents: "none",
          }} />
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Typography sx={{
              fontSize: { xs: 11, sm: 12 }, fontWeight: 700,
              letterSpacing: 1.5, textTransform: "uppercase",
              color: "rgba(255,255,255,0.7)", mb: 1,
            }}>
              Dashboard
            </Typography>
            <Typography sx={{
              fontWeight: 800,
              fontSize: { xs: 24, sm: 32, md: 40 },
              color: "#fff", lineHeight: 1.15,
              mb: { xs: 1, sm: 1.5 }, letterSpacing: -0.5,
            }}>
              Welcome back, {firstName}
            </Typography>
            <Typography sx={{
              color: "rgba(255,255,255,0.85)",
              fontSize: { xs: 13, sm: 15, md: 16 },
              maxWidth: 520, lineHeight: 1.5,
            }}>
              Continue upskilling. Connect with mentors. Join a community.
            </Typography>
          </Box>
        </Paper>
      </Box>

      {/* ══ QUICK ACTIONS — white tiles, blue icons, green labels ══════════ */}
      <Box sx={{ px, mb: rowMb }}>
        <Panel>
          <PanelHeader title="Quick Actions" />
          {[actionsTop, actionsBottom].map((row, ri) => (
            <Box key={ri} sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2,1fr)", sm: "repeat(4,1fr)" },
              gap: { xs: 1, sm: 1.5 },
              mb: ri === 0 ? { xs: 1, sm: 1.5 } : 0,
            }}>
              {row.map((action, i) => (
                <ActionTile
                  key={i}
                  label={action.label}
                  icon={action.icon}
                  onClick={() => action.path && navigate(action.path)}
                />
              ))}
            </Box>
          ))}
        </Panel>
      </Box>

      {/* ══ UPCOMING SESSIONS + LATEST LESSONS ═════════════════════════════ */}
      <Box sx={{
        px, mb: rowMb,
        display: "flex",
        flexDirection: { xs: "column", lg: "row" },
        gap: { xs: 2, lg: 2.5 },
        alignItems: "stretch",
      }}>

        {/* ── Upcoming Sessions ── */}
        <Panel sx={{ flex: { lg: "0 0 59%" }, minWidth: 0 }}>
          <PanelHeader
            title="Upcoming Sessions"
            count={sessions.length}
            onViewAll={() => navigate("/sessions")}
          />
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <CircularProgress size={22} sx={{ color: C.secondary }} />
            </Box>
          ) : error ? (
            <Typography color="error" sx={{ fontSize: 12 }}>{error}</Typography>
          ) : sessions.length === 0 ? (
            <Stack alignItems="center" spacing={1} py={3}>
              <EventIcon sx={{ fontSize: 36, color: C.textMuted, opacity: 0.5 }} />
              <Typography sx={{ color: C.textSecondary, fontSize: 13 }}>No upcoming sessions</Typography>
            </Stack>
          ) : sessions.map((s, i) => (
            <RowItem key={s._id || i}>
              {/* Avatar: soft-blue bg, blue initial */}
              <Avatar sx={{
                width: { xs: 36, sm: 42 }, height: { xs: 36, sm: 42 },
                flexShrink: 0,
                bgcolor: C.primarySoft,
                color: C.primary,
                fontWeight: 700, fontSize: { xs: 13, sm: 15 },
              }}>
                {s.title?.charAt(0)?.toUpperCase() || "S"}
              </Avatar>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{
                  fontWeight: 700, fontSize: { xs: 12, sm: 13 },
                  color: C.textPrimary,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {s.title || "Session"}
                </Typography>
                <Typography sx={{ fontSize: { xs: 10, sm: 11 }, color: C.textMuted, mt: 0.25 }}>
                  {new Date(s.date).toLocaleDateString()} · {s.startTime} – {s.endTime}
                </Typography>
              </Box>

              <Box sx={{ textAlign: "center", flexShrink: 0, display: { xs: "none", sm: "block" } }}>
                <Typography sx={{ fontSize: 9, color: C.textMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Duration
                </Typography>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.textPrimary }}>
                  {s.duration || "N/A"}
                </Typography>
              </Box>

              <Box sx={{ textAlign: "center", flexShrink: 0, display: { xs: "none", md: "block" } }}>
                <Typography sx={{ fontSize: 9, color: C.textMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Platform
                </Typography>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.textPrimary }}>
                  {s.platform || "N/A"}
                </Typography>
              </Box>

              <SecondaryBtn label="Join" onClick={() => window.open(s.meetingLink, "_blank")} />
            </RowItem>
          ))}
        </Panel>

        {/* ── Latest Lessons ── */}
        <Panel sx={{ flex: 1, minWidth: 0 }}>
          <PanelHeader title="Latest Lessons" count={lessons.length} onViewAll={() => navigate("/curriculum")} />

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <CircularProgress size={22} sx={{ color: C.secondary }} />
            </Box>
          )}
          {error && <Typography sx={{ color: "red", fontSize: 12 }}>Error: {error}</Typography>}
          {!loading && !error && lessons.length === 0 && (
            <Stack alignItems="center" spacing={1} py={3}>
              <PlayCircle sx={{ fontSize: 36, color: C.textMuted, opacity: 0.5 }} />
              <Typography sx={{ color: C.textSecondary, fontSize: 13 }}>No lessons yet</Typography>
            </Stack>
          )}

          {lessons.map((lesson, i) => (
            <RowItem key={lesson._id || i} onClick={() => navigate("/curriculum")}>
              {/* Icon box: soft-green tint, green icon */}
              <Box sx={{
                flexShrink: 0,
                width: { xs: 36, sm: 42 }, height: { xs: 36, sm: 42 },
                borderRadius: 1.5,
                display: "flex", alignItems: "center", justifyContent: "center",
                bgcolor: C.secondarySoft,
                color: C.secondary,
              }}>
                <PlayCircle sx={{ fontSize: { xs: 20, sm: 22 } }} />
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{
                  fontWeight: 700, fontSize: { xs: 12, sm: 13 },
                  color: C.textPrimary, mb: 0.3,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {lesson.title}
                </Typography>
                <Typography sx={{
                  fontSize: { xs: 10, sm: 11 }, color: C.textSecondary,
                  overflow: "hidden", display: "-webkit-box",
                  WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                  lineHeight: 1.4,
                }}>
                  {lesson.description?.slice(0, 100)}...
                </Typography>
              </Box>
              <SecondaryBtn label="View" onClick={(e) => { e.stopPropagation(); navigate("/curriculum"); }} />
            </RowItem>
          ))}
        </Panel>
      </Box>

      {/* ══ COMMUNITY ACTIVITY FEED ═════════════════════════════════════════ */}
      <Box sx={{ px, mb: { xs: 3, sm: 5 } }}>
        <Panel>
          <PanelHeader title="Community Activity Feed" onViewAll={() => navigate("/discussionForum")} />

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <CircularProgress size={22} sx={{ color: C.secondary }} />
            </Box>
          ) : error ? (
            <Typography color="error" sx={{ fontSize: 12 }}>{error}</Typography>
          ) : currentPosts.length === 0 ? (
            <Stack alignItems="center" spacing={1} py={3}>
              <Forum sx={{ fontSize: 36, color: C.textMuted, opacity: 0.5 }} />
              <Typography sx={{ color: C.textSecondary, fontSize: 13 }}>No posts yet</Typography>
            </Stack>
          ) : currentPosts.map((post) => (
            <Card key={post._id} elevation={0} sx={{
              mb: 1.5, borderRadius: 2,
              border: `1px solid ${C.borderSoft}`,
              width: "100%", boxSizing: "border-box",
              transition: "all 0.15s ease",
              "&:hover": { borderColor: C.secondarySoft, bgcolor: C.secondarySofter },
            }}>
              <CardContent sx={{ p: { xs: "14px", sm: "18px" }, "&:last-child": { pb: { xs: "14px", sm: "18px" } } }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1.5, gap: 1.25 }}>
                  {/* Avatar: soft-green bg, green initial */}
                  <Avatar sx={{
                    width: { xs: 32, sm: 38 }, height: { xs: 32, sm: 38 },
                    flexShrink: 0,
                    bgcolor: C.secondarySoft,
                    color: C.secondary,
                    fontWeight: 700, fontSize: { xs: 13, sm: 15 },
                  }}>
                    {post.createdBy?.email?.charAt(0).toUpperCase() || "U"}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{
                      fontWeight: 700, fontSize: { xs: 12, sm: 13 },
                      color: C.textPrimary,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      {post.createdBy?.email || "Unknown User"}
                    </Typography>
                    {post.category && (
                      <Chip label={post.category} size="small" sx={{
                        height: 18, fontSize: 10, fontWeight: 700,
                        bgcolor: C.secondarySoft, color: C.secondary,
                        mt: 0.25, "& .MuiChip-label": { px: 0.75 },
                      }} />
                    )}
                  </Box>
                  <Typography sx={{ color: C.textMuted, fontSize: { xs: 10, sm: 11 }, flexShrink: 0 }}>
                    {new Date(post.createdAt).toLocaleDateString()}
                  </Typography>
                </Box>

                <Typography sx={{
                  fontWeight: 700, fontSize: { xs: 13, sm: 15, md: 16 },
                  mb: 1.5, lineHeight: 1.5, color: C.textPrimary,
                }}>
                  {post.title?.slice(0, 60)}
                </Typography>

                <SecondaryBtn label="View Post" onClick={() => navigate("/discussionForum")} />
              </CardContent>
            </Card>
          ))}
        </Panel>
      </Box>

      <Footer />
    </Box>
  );
};